import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Receipt, Upload, Clock, CheckCircle, XCircle, FileText, Download, Loader2, Euro, AlertCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente facture', class: 'bg-amber-100 text-amber-700', icon: Clock },
  invoice_uploaded: { label: 'Facture soumise', class: 'bg-blue-100 text-blue-700', icon: FileText },
  invoice_rejected: { label: 'Facture a corriger', class: 'bg-red-100 text-red-700', icon: XCircle },
  valide_30j: { label: 'Valide - En attente 30j', class: 'bg-purple-100 text-purple-700', icon: Clock },
  payment_pending: { label: 'En attente paiement', class: 'bg-cyan-100 text-cyan-700', icon: Clock },
  en_cours_paiement: { label: 'En cours de paiement', class: 'bg-indigo-100 text-indigo-700', icon: Euro },
  paid: { label: 'Payee', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

export const Invoices = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      const response = await fetch(`${API}/commercial/eligible-commissions`, { credentials: 'include' });
      if (response.ok) {
        setCommissions(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const openUpload = (commission) => {
    setSelectedCommission(commission);
    setSelectedFile(null);
    setIsUploadOpen(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Veuillez selectionner un fichier');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API}/commercial/commissions/${selectedCommission.commission_id}/upload-invoice`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Facture envoyee avec succes');
        setIsUploadOpen(false);
        fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setUploading(false);
    }
  };

  const downloadInvoice = async (commissionId, filename) => {
    setDownloading(commissionId);
    try {
      const response = await fetch(`${API}/commissions/${commissionId}/download-invoice`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'facture.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Facture telechargee');
      } else {
        toast.error('Erreur de telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur de telechargement');
    } finally {
      setDownloading(null);
    }
  };

  // Calculate totals
  const toInvoice = commissions.filter(c => ['pending', 'invoice_rejected'].includes(c.status)).reduce((sum, c) => sum + (c.amount || 0), 0);
  const waiting30d = commissions.filter(c => c.status === 'valide_30j').reduce((sum, c) => sum + (c.amount || 0), 0);
  const inPayment = commissions.filter(c => ['en_cours_paiement', 'payment_pending'].includes(c.status)).reduce((sum, c) => sum + (c.amount || 0), 0);
  const paid = commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + (c.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="invoices-title">
            Mes factures
          </h1>
          <p className="text-slate-500 mt-1">Deposez vos factures pour les commissions eligibles</p>
        </div>
      </div>

      {/* Workflow explanation */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">Comment ca marche ?</p>
              <ol className="text-sm text-amber-700 mt-1 list-decimal list-inside space-y-1">
                <li>Une fois votre vente validee, elle apparait ici</li>
                <li>Deposez votre facture pour validation</li>
                <li>Une fois validee, l'administration procede au paiement sous 30 jours</li>
                <li>Une fois paye, la commission est ajoutee a votre CA</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100">
                <Upload className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">A facturer</p>
                <p className="text-2xl font-bold text-amber-600">{toInvoice.toLocaleString('fr-FR')} €</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-100">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Valide - 30j</p>
                <p className="text-2xl font-bold text-purple-600">{waiting30d.toLocaleString('fr-FR')} €</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-cyan-100">
                <Euro className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En cours paiement</p>
                <p className="text-2xl font-bold text-cyan-600">{inPayment.toLocaleString('fr-FR')} €</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-100">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Deja paye</p>
                <p className="text-2xl font-bold text-emerald-600">{paid.toLocaleString('fr-FR')} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commissions Table */}
      <Card className="border-0 shadow-sm" data-testid="commissions-table">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Commissions a facturer
          </CardTitle>
          <CardDescription>Deposez vos factures pour ces commissions</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Vente</TableHead>
                <TableHead>Montant commission</TableHead>
                <TableHead>Taux</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : commissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    Aucune commission
                  </TableCell>
                </TableRow>
              ) : (
                commissions.map((comm) => {
                  const StatusIcon = statusConfig[comm.status]?.icon || Clock;
                  const canUpload = ['pending', 'invoice_rejected'].includes(comm.status);
                  
                  return (
                    <TableRow key={comm.commission_id} className="hover:bg-slate-50">
                      <TableCell>
                        <div>
                          <p className="font-medium">{comm.client_name || 'Client'}</p>
                          <p className="text-sm text-slate-500">{comm.offer || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-bold text-emerald-600">
                        {comm.amount?.toLocaleString('fr-FR')} €
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{comm.rate}%</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[comm.status]?.class || 'bg-slate-100'} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[comm.status]?.label || comm.status}
                        </Badge>
                        {comm.status === 'invoice_rejected' && comm.rejection_comment && (
                          <p className="text-xs text-red-600 mt-1">{comm.rejection_comment}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {canUpload && (
                            <Button
                              size="sm"
                              onClick={() => openUpload(comm)}
                              className="bg-indigo-600 hover:bg-indigo-700"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              {comm.status === 'invoice_rejected' ? 'Renvoyer' : 'Deposer'} facture
                            </Button>
                          )}
                          {comm.invoice_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadInvoice(comm.commission_id, comm.invoice_filename)}
                              disabled={downloading === comm.commission_id}
                            >
                              {downloading === comm.commission_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-1" />
                                  Voir
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deposer une facture</DialogTitle>
            <DialogDescription>
              Televersez votre facture pour la commission de {selectedCommission?.amount?.toLocaleString('fr-FR')} €
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedCommission?.status === 'invoice_rejected' && selectedCommission?.rejection_comment && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800">Motif du renvoi:</p>
                <p className="text-sm text-red-600">{selectedCommission.rejection_comment}</p>
              </div>
            )}
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="hidden"
                id="invoice-upload"
              />
              <label htmlFor="invoice-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 font-medium">
                  {selectedFile ? selectedFile.name : 'Cliquez pour telecharger votre facture'}
                </p>
                <p className="text-sm text-slate-500 mt-1">PDF, DOC ou DOCX</p>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFile}>
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
